/**
 * Google Login Button Component
 *
 * Uses @react-oauth/google to render a Google Sign-In button.
 * Handles the OAuth flow and stores tokens on successful authentication.
 */

import { GoogleLogin, CredentialResponse } from '@react-oauth/google';
import { useNotify, useRedirect } from 'react-admin';
import { Box, Typography, Alert } from '@mui/material';
import config from '../config';
import Logger from '../utils/Logger';

interface GoogleLoginButtonProps {
  /** Callback on successful login */
  onSuccess?: () => void;
  /** Callback on login failure */
  onError?: (error: string) => void;
  /** Callback when MFA setup is required (mfaRequired=true but mfaEnabled=false) */
  onMfaSetupRequired?: () => void;
}

/**
 * Google Login Button
 *
 * Renders a Google Sign-In button that:
 * 1. Gets ID token from Google
 * 2. Sends it to backend /auth/oauth/google
 * 3. Stores returned JWTs in localStorage
 * 4. Redirects to dashboard on success
 */
export const GoogleLoginButton = ({ onSuccess, onError, onMfaSetupRequired }: GoogleLoginButtonProps) => {
  const notify = useNotify();
  const redirect = useRedirect();

  // Check if Google OAuth is configured
  const isConfigured = Boolean(config.GOOGLE_CLIENT_ID);

  const handleGoogleSuccess = async (credentialResponse: CredentialResponse) => {
    Logger.log('[GoogleLogin] Google sign-in successful, processing...');


    if (!credentialResponse.credential) {
      Logger.error('[GoogleLogin] No credential in response');
      notify('Google sign-in failed: No credential received', { type: 'error' });
      onError?.('No credential received');
      return;
    }

    try {
      // Send Google ID token to backend
      const response = await fetch(`${config.API_URL}/auth/oauth/google`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          idToken: credentialResponse.credential,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.message || `Server error: ${response.status}`;
        // Logger.error('[GoogleLogin] Backend error:', errorMessage); // Refactored: Removed this log line
        notify(`Sign in failed: ${errorMessage}`, { type: 'error' });
        onError?.(errorMessage);
        return;
      }

      const tokens = await response.json();

      // Store tokens (same as password login)
      localStorage.setItem('token', tokens.idToken);
      localStorage.setItem('refreshToken', tokens.refreshToken);
      if (tokens.accessToken) {
        localStorage.setItem('accessToken', tokens.accessToken);
      }
      if (tokens.deviceKey) {
        localStorage.setItem('deviceKey', tokens.deviceKey);
      }

      Logger.log('[GoogleLogin] Login successful, tokens stored');

      // Check if MFA setup is required (admin forced MFA but user hasn't set it up yet)
      if (tokens.requiresMfaSetup) {
        Logger.log('[GoogleLogin] MFA setup required');
        notify('Please set up two-factor authentication to continue', { type: 'info' });
        onMfaSetupRequired?.();
        return;
      }

      notify('Signed in successfully', { type: 'success' });

      onSuccess?.();

      // Redirect to dashboard
      redirect('/');
    } catch (error) {
      Logger.error('[GoogleLogin] Network error:', error);
      const message = error instanceof Error ? error.message : 'Network error';
      notify(`Sign in failed: ${message}`, { type: 'error' });
      onError?.(message);
    }
  };

  const handleGoogleError = () => {
    Logger.error('[GoogleLogin] Google sign-in error');
    notify('Google sign-in was cancelled or failed', { type: 'warning' });
    onError?.('Google sign-in failed');
  };

  // Don't render if not configured
  if (!isConfigured) {
    return (
      <Box sx={{ mt: 2, width: '100%' }}>
        <Alert severity="info" sx={{ fontSize: '0.75rem' }}>
          Google Sign-In is not configured. Contact administrator.
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
        mb: 1,
      }}
    >
      {/* Standard GoogleLogin button - auto_select disabled to prevent auto-login with cached account */}
      <GoogleLogin
        onSuccess={handleGoogleSuccess}
        onError={handleGoogleError}
        useOneTap={false}
        auto_select={false}
        theme="outline"
        size="large"
        text="signin_with"
        shape="rectangular"
        ux_mode="popup"
      />

      {/* Helper text for switching accounts */}
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ mt: 0.5, fontSize: '0.7rem', textAlign: 'center' }}
      >
        Not seeing your account? <a
          href="https://accounts.google.com/logout"
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: 'inherit', textDecoration: 'underline' }}
        >
          Sign out of Google
        </a> first, then try again.
      </Typography>
    </Box>
  );
};

export default GoogleLoginButton;
